import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { useMemo } from "react";
import StatusIndicator, {
  IndicatorProps,
} from "components/lib/statusIndicator";
import { TableMessage } from "./types";
import { FALLBACK_VALUE } from "components/Table";

const formatMessage = (
  messageStatus: TableMessage["status"],
  t: TFunction,
): [string, errorKey: IndicatorProps["variant"]] => {
  if (messageStatus?.isDeleted) {
    return [t("TABLE_STATUS_DELETED"), "error"];
  }
  if (messageStatus?.isViewed) {
    return [t("TABLE_STATUS_READ"), "primary"];
  }

  return [t("TABLE_STATUS_NOT_READ"), "error"];
};

export const useMessageTableData = (data: TableMessage[]) => {
  const { t } = useTranslation();

  const emptyRow: [TableMessage] = [
    {
      id: FALLBACK_VALUE,
      title: FALLBACK_VALUE,
      messageTemplate: FALLBACK_VALUE,
      sentBy: FALLBACK_VALUE,
      date: FALLBACK_VALUE,
      status: null,
    },
  ];

  const rows = data.length > 0 ? data : emptyRow;

  const columns = useMemo<ColumnDef<TableMessage>[]>(() => {
    return [
      {
        header: "PATIENT_MESSAGE_TABLE_HEADER_TITLE",
        accessorKey: "title",
        cell: (info) => info.getValue(),
      },
      {
        header: "PATIENT_MESSAGE_TABLE_HEADER_MESSAGE_TEMPLATE",
        accessorKey: "messageTemplate",
        cell: (info) => info.getValue(),
      },
      {
        header: "PATIENT_MESSAGE_TABLE_HEADER_STATUS",
        accessorKey: "status",
        cell: ({ getValue }) => {
          const status = getValue() as TableMessage["status"];
          const [messageStatus, variant] = formatMessage(status, t);
          if (!status) return FALLBACK_VALUE;

          return (
            <>
              <StatusIndicator
                data-testid="test-statusIndicator-error"
                variant={variant}
              />
              {messageStatus}
            </>
          );
        },
      },
      {
        header: "PATIENT_MESSAGE_TABLE_HEADER_SENT_BY",
        accessorKey: "sentBy",
        cell: (info) => info.getValue(),
      },
      {
        header: "PATIENT_MESSAGE_TABLE_HEADER_DATE",
        accessorKey: "date",
        cell: (info) => info.getValue(),
      },
    ];
  }, [t]);

  return { columns, rows };
};
